import posthog from "posthog-js";
import type { SponsorTier } from "@wondermaps/db";

// =============================================================================
// Throttling - Prevent PostHog event spam
// =============================================================================
const THROTTLE_MS: Record<string, number> = {
  select_poi: 2000,       // Rapid clicks
  filter_category: 2000,  // Rapid toggles
  nearby_sort: 3000,      // Mode switches
  search_performed: 5000, // Already debounced 1.5s in UI
  nearest_quick_action: 3000,
  interaction_gps: 5000,
  bookmark_poi: 2000,     // Rapid bookmark toggles
  unbookmark_poi: 2000,   // Rapid bookmark toggles
  poi_status_changed: 2000, // Status toggle
  select_event: 2000,     // Rapid event clicks
  filter_happening_now: 2000, // Filter toggle
  // view_map, share_map, click_outbound, engage_poi_content, view_events_list: 0 (important metrics)
};

const lastFired = new Map<string, number>();

function throttledCapture(event: string, props: Record<string, unknown>) {
  const throttle = THROTTLE_MS[event] ?? 0;
  if (throttle > 0) {
    const now = Date.now();
    if (now - (lastFired.get(event) || 0) < throttle) return;
    lastFired.set(event, now);
  }
  posthog.capture(event, props);
}

// =============================================================================
// Initialize PostHog
export function initAnalytics() {
  const apiKey = import.meta.env.VITE_POSTHOG_KEY;
  const apiHost = import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com";

  if (!apiKey) {
    console.warn("[Analytics] VITE_POSTHOG_KEY not set, analytics disabled");
    return;
  }

  posthog.init(apiKey, {
    api_host: apiHost,
    // ui_host is needed for PostHog toolbar when using a proxy
    ui_host: "https://us.posthog.com",
    capture_pageview: false, // We'll handle this manually for SPAs
    capture_pageleave: true, // Suppressed when opted out — opt_out_capturing() gates all capture calls
    persistence: "memory", // No cookies until consent given
  });

  applyStoredConsent();
}

// Check localStorage for stored consent and apply opt-in/opt-out
// Key matches useAnalyticsConsent hook: "wm_analytics_consent"
export function applyStoredConsent() {
  const consent = localStorage.getItem("wm_analytics_consent");
  if (consent === "accepted") {
    posthog.opt_in_capturing();
  } else {
    // Default to opted-out until explicit consent (GDPR opt-in model)
    posthog.opt_out_capturing();
  }
}

// Context that gets attached to all events
let globalContext: {
  org_slug?: string;
  map_slug?: string;
  map_status?: "active" | "paused";
} = {};

export function setAnalyticsContext(ctx: {
  org_slug?: string;
  map_slug?: string;
  map_status?: "active" | "paused";
}) {
  globalContext = { ...globalContext, ...ctx };
}

/** Read current analytics context (org_slug, map_slug) for external callers like $pageview. */
export function getAnalyticsContext() {
  return { ...globalContext };
}

// Helper to merge global context with event properties
function withContext<T extends Record<string, unknown>>(props: T) {
  return { ...globalContext, ...props };
}

// ============================================
// TYPED EVENT DEFINITIONS
// ============================================

// Traffic & Attribution
export type ViewMapProps = { ref?: string };
export type ShareMapProps = { method: "native" | "clipboard" };

// Search Intent
export type SearchPerformedProps = { query: string; result_count: number };
export type SearchConvertedProps = { query: string; poi_id: string; poi_name: string };

// Engagement
export type SelectPoiProps = {
  poi_id: string;
  poi_name: string;
  category?: string;
  method: "map_click" | "list_click" | "search_click" | "featured_click";
  // Sponsor fields for analytics
  is_sponsored?: boolean;
  sponsor_tier?: SponsorTier | null;
};
export type FilterCategoryProps = { category_id: string; category_name: string; action: "add" | "remove" | "clear_all" };
export type EngagePoiContentProps = {
  poi_id: string;
  poi_name: string;
  duration_seconds: number;
  // Sponsor fields for analytics
  is_sponsored?: boolean;
  sponsor_tier?: SponsorTier | null;
};
export type InteractionGpsProps = { trigger: "sort_by_distance" | "locate_me" };

// Nearby / Location
export type NearbySortProps = { mode: "default" | "distance" | "category" };
export type NearestQuickActionProps = { category: string };

// Conversion
export type ClickOutboundProps = {
  url: string;
  label?: string;
  location: "poi_button" | "hamburger_menu" | "social_links" | "more_cta" | "more_quick_link";
};
export type InteractionNavigateProps = { poi_id: string; poi_name: string; destination: string };

// Bookmarks
export type BookmarkPoiProps = { poi_id: string; poi_name: string; source: "list" | "detail" | "map" };
export type UnbookmarkPoiProps = { poi_id: string; poi_name: string; source: "list" | "detail" | "map" };

// POI Status
export type PoiStatusChangedProps = {
  poi_id: string;
  poi_name: string;
  old_status: string;
  new_status: string;
  reason?: string;
};

// Visitor Feedback
export type VisitorFeedbackProps = {
  rating: number;
  comment?: string;
  gps_session_minutes: number;
  pois_viewed: number;
  device_type: "mobile" | "tablet" | "desktop";
};

// Curated Lists
export type CuratedListLoadedProps = {
  list_slug: string;
  list_name: string;
  poi_count: number;
  source: "url" | "browse";
};

export type CuratedListPreviewedProps = {
  list_slug: string;
  list_name: string;
  poi_count: number;
};

// Sponsors
export type SponsorImpressionProps = { sponsor_id: string; sponsor_name: string; placement: string };
export type SponsorClickProps = { sponsor_id: string; sponsor_name: string; placement: string; website_url?: string };

// Emergency Actions
export type EmergencyActionProps = {
  action_id: string;
  action_type: "call" | "navigate" | "category";
  action_label: string;
};

// Events
export type ViewEventsListProps = { happening_now_count: number; total_count: number };
export type SelectEventProps = { event_id: string; event_name: string; poi_id?: string; status: "happening-now" | "today-upcoming" | "upcoming" };
export type FilterHappeningNowProps = { poi_count: number };
export type EventLinkClickProps = { event_id: string; event_name: string; url: string; poi_id?: string };

// Notifications (IDs + enums only — never title/body to avoid PII leakage)
export type NotificationsTrayOpenedProps = { unread_count: number; total_count: number };
export type NotificationReadProps = {
  notification_id: string;
  kind?: string;
  type: "info" | "warning" | "urgent";
  was_unread: boolean;
};
export type NotificationsMarkAllReadProps = { unread_count_before: number };
export type NotificationRowExpandedProps = {
  notification_id: string;
  kind?: string;
  type: "info" | "warning" | "urgent";
};

// ============================================
// ANALYTICS API
// ============================================

export const analytics = {
  // Generic track for one-off events (uses withContext for org/map context)
  track: (event: string, props: Record<string, unknown> = {}) => {
    throttledCapture(event, withContext(props));
  },

  // Traffic & Attribution (0ms throttle - always fire)
  viewMap: (props: ViewMapProps = {}) => {
    throttledCapture("view_map", withContext(props));
  },

  shareMap: (props: ShareMapProps) => {
    throttledCapture("share_map", withContext(props));
  },

  // Search Intent
  searchPerformed: (props: SearchPerformedProps) => {
    throttledCapture("search_performed", withContext(props));
  },

  searchConverted: (props: SearchConvertedProps) => {
    throttledCapture("search_converted", withContext(props));
  },

  // Engagement
  selectPoi: (props: SelectPoiProps) => {
    throttledCapture("select_poi", withContext(props));
  },

  filterCategory: (props: FilterCategoryProps) => {
    throttledCapture("filter_category", withContext(props));
  },

  engagePoiContent: (props: EngagePoiContentProps) => {
    throttledCapture("engage_poi_content", withContext(props));
  },

  interactionGps: (props: InteractionGpsProps) => {
    throttledCapture("interaction_gps", withContext(props));
  },

  nearbySort: (props: NearbySortProps) => {
    throttledCapture("nearby_sort", withContext(props));
  },

  nearestQuickAction: (props: NearestQuickActionProps) => {
    throttledCapture("nearest_quick_action", withContext(props));
  },

  // Conversion (0ms throttle - always fire)
  clickOutbound: (props: ClickOutboundProps) => {
    throttledCapture("click_outbound", withContext(props));
  },

  interactionNavigate: (props: InteractionNavigateProps) => {
    throttledCapture("interaction_navigate", withContext(props));
  },

  // Bookmarks
  bookmarkPoi: (props: BookmarkPoiProps) => {
    throttledCapture("bookmark_poi", withContext(props));
  },

  unbookmarkPoi: (props: UnbookmarkPoiProps) => {
    throttledCapture("unbookmark_poi", withContext(props));
  },

  // Events
  viewEventsList: (props: ViewEventsListProps) => {
    throttledCapture("view_events_list", withContext(props));
  },

  selectEvent: (props: SelectEventProps) => {
    throttledCapture("select_event", withContext(props));
  },

  filterHappeningNow: (props: FilterHappeningNowProps) => {
    throttledCapture("filter_happening_now", withContext(props));
  },

  eventLinkClick: (props: EventLinkClickProps) => {
    throttledCapture("event_link_click", withContext(props));
  },

  // POI Status
  poiStatusChanged: (props: PoiStatusChangedProps) => {
    throttledCapture("poi_status_changed", withContext(props));
  },

  // Sponsors (0ms throttle - always fire, important for sales metrics)
  sponsorImpression: (props: SponsorImpressionProps) => {
    throttledCapture("sponsor_impression", withContext(props));
  },

  sponsorClick: (props: SponsorClickProps) => {
    throttledCapture("sponsor_click", withContext(props));
  },

  // Curated Lists
  curatedListLoaded: (props: CuratedListLoadedProps) => {
    throttledCapture("curated_list_loaded", withContext(props));
  },
  curatedListPreviewed: (props: CuratedListPreviewedProps) => {
    throttledCapture("curated_list_previewed", withContext(props));
  },

  // Emergency Actions (0ms throttle - safety metric, always fire)
  emergencyAction: (props: EmergencyActionProps) => {
    throttledCapture("emergency_action", withContext(props));
  },

  // Visitor Feedback
  visitorFeedback: (props: VisitorFeedbackProps) => {
    throttledCapture("visitor_feedback", withContext(props));
  },

  // Notifications
  notificationsTrayOpened: (props: NotificationsTrayOpenedProps) => {
    throttledCapture("notifications_tray_opened", withContext(props));
  },

  notificationRead: (props: NotificationReadProps) => {
    throttledCapture("notification_read", withContext(props));
  },

  notificationsMarkAllRead: (props: NotificationsMarkAllReadProps) => {
    throttledCapture("notifications_mark_all_read", withContext(props));
  },

  notificationRowExpanded: (props: NotificationRowExpandedProps) => {
    throttledCapture("notification_row_expanded", withContext(props));
  },
};

// Export posthog instance for advanced usage
export { posthog };
